import type { FC } from 'react'

import { ExternalLink } from '@skytech/ui/ExternalLink'

import { Translation } from '@/app/translations/Translation'
import DataPoints, { DataPoint } from '@/components/DataPoints'
import { LocalDate } from '@/components/LocalDate'
import { LocalDateTime } from '@/components/LocalDateTime'
import { phoneNumber as getPhoneNumber } from '@/util/formatters/text'
import { FORM_URL } from '@/util/misc'

import type { InjuryReportExtended } from './types'

const InjuryReport: FC<{ injuryReport: InjuryReportExtended }> = ({ injuryReport }) => {
  const {
    assetDescription,
    assetImeiSnr,
    userName,
    phoneNumber,
    created,
    updated,
    damageLocation,
    damageCause,
    injuryDate,
    policeReportRef,
    insuranceRef,
    damageDescription,
    creatorName,
    editorName,
    type,
    code,
  } = injuryReport

  return (
    <div className="sections">
      <section>
        <DataPoints>
          <h3>
            <Translation id="User" />
          </h3>

          <DataPoint header={<Translation id="Name" />} value={userName} />

          <DataPoint
            header={<Translation id="Phone number" />}
            value={getPhoneNumber(phoneNumber)}
          />
        </DataPoints>
      </section>

      <section>
        <DataPoints>
          <h3>
            <Translation id="Asset" />
          </h3>

          <DataPoint header={<Translation id="Description" />} value={assetDescription || '—'} />

          <DataPoint header={<Translation id="Imei/snr" />} value={assetImeiSnr || '—'} />
        </DataPoints>
      </section>

      <section>
        <DataPoints>
          <h3>
            <Translation id="Injury" />
          </h3>

          <DataPoint header={<Translation id="Type" />} value={type || '—'} />

          <DataPoint header={<Translation id="Location" />} value={damageLocation || '—'} />

          <DataPoint header={<Translation id="Date" />} value={<LocalDate date={injuryDate} />} />

          <DataPoint header={<Translation id="Cause" />} value={damageCause || '—'} />
          {damageDescription && (
            <DataPoint header={<Translation id="Description" />} value={damageDescription || '—'} />
          )}
        </DataPoints>
      </section>

      <section>
        <DataPoints>
          <h3>
            <Translation id="Injury Report" />
          </h3>

          <DataPoint
            header={<Translation id="Created" />}
            value={<LocalDateTime date={created} />}
          />

          <DataPoint
            header={<Translation id="Updated" />}
            value={<LocalDateTime date={updated} />}
          />

          <DataPoint header={<Translation id="Created by" />} value={creatorName || userName} />

          <DataPoint header={<Translation id="Updated by" />} value={editorName || userName} />
          {insuranceRef && (
            <DataPoint header={<Translation id="Insurance ref" />} value={insuranceRef} />
          )}

          {policeReportRef && (
            <DataPoint header={<Translation id="Police ref" />} value={policeReportRef} />
          )}
          <DataPoint
            header={<Translation id="Form" />}
            value={
              <ExternalLink isWhiteLabeled withIcon href={FORM_URL.replace(':code', code)}>
                <Translation id="View" />
              </ExternalLink>
            }
          />
        </DataPoints>
      </section>
    </div>
  )
}

export default InjuryReport
